/**
 * Recovers the HTML body Outlook hides inside a `.msg` file's RTF.
 *
 * ⚠️ WHY THIS EXISTS. A `.msg` does not store its HTML body as HTML. Outlook compresses the body
 * into `PidTagRtfCompressed` as RTF that *encapsulates* the original HTML (MS-OXRTFEX): the markup
 * is carried in `\*\htmltag` destinations interleaved with the text, and RTF-only formatting is
 * fenced off with `\htmlrtf` so a reader can drop it. Without this module the `.msg` viewer has no
 * markup at all, so a `cid:`-referenced inline image has nothing to render into — which is the
 * whole reason embedded images never appeared.
 *
 * ⚠️ THIS FUNCTION'S OUTPUT IS UNTRUSTED. Emitting arbitrary markup is its *job*, so it is an XSS
 * source by construction. It is not a sanitiser and must never be treated as one. Every caller MUST
 * pass the result through `emailBodySanitiser.sanitiseBodyHtml` before it reaches the viewer's
 * frame. ⛔ Do not render this directly, and do not add a "trusted" fast path.
 *
 * The parse is deliberately *lossy in one direction only*: anything it cannot interpret is dropped
 * rather than guessed at, and any failure returns null so the adapter falls back to the plain-text
 * body. A message this module cannot read renders exactly as it did before this module existed.
 */

import { decompressRTF } from '@kenjiuno/decompressrtf';

/**
 * Byte ceiling on decompressed RTF, above which the body is refused rather than parsed.
 *
 * RTF decompression is an expansion step on attacker-supplied bytes — a real message expands ~4.7×
 * — so the output needs its own bound rather than inheriting the compressed input's. Mirrors
 * `EML_MAX_PARSE_BYTES`; a real Outlook body runs ~42 KB, so this is orders of magnitude of slack.
 */
export const RTF_MAX_DECOMPRESSED_BYTES = 10 * 1024 * 1024;

/**
 * RTF declares its byte encoding as a Windows code page. Only the families that actually appear in
 * mail are mapped; anything else falls back to cp1252, which is what Outlook writes by default.
 */
const CODE_PAGE_LABELS: ReadonlyMap<string, string> = new Map([
  ['932', 'shift_jis'],
  ['936', 'gbk'],
  ['949', 'euc-kr'],
  ['950', 'big5'],
  ['1250', 'windows-1250'],
  ['1251', 'windows-1251'],
  ['1252', 'windows-1252'],
  ['1253', 'windows-1253'],
  ['1254', 'windows-1254'],
  ['1255', 'windows-1255'],
  ['1256', 'windows-1256'],
  ['1257', 'windows-1257'],
  ['1258', 'windows-1258'],
  ['10000', 'macintosh'],
  ['65001', 'utf-8']
]);

/** Chunk size for byte→string scanning. Bounded so `String.fromCharCode` is not given a huge spread. */
const SCAN_CHUNK = 0x8000;

/**
 * Destinations whose contents are RTF's own bookkeeping, never body text.
 *
 * ⚠️ These carry NO `\*` marker, which is the trap. `\*` means "a reader that does not understand
 * this destination may skip it", so it is tempting to treat `\*` as the whole rule — but the font
 * and colour tables are destinations every reader is expected to *understand*, and they are written
 * bare. Skipping only `\*` destinations therefore emits the font table's text into the body, and a
 * real Outlook message opens with `MS PGothic;MS Gothic;Symbol;Courier New;` before its first word.
 *
 * Caught by a real specimen, not by synthetic fixtures — the synthetic tests asserted only that the
 * expected text was present, never that this text was absent.
 */
const SKIPPED_DESTINATIONS: ReadonlySet<string> = new Set([
  'fonttbl',
  'colortbl',
  'stylesheet',
  'listtable',
  'listoverridetable',
  'rsidtable',
  'info',
  'filetbl',
  'revtbl',
  'pntext',
  'pntxta',
  'pntxtb',
  'generator',
  'themedata',
  'latentstyles',
  'datastore',
  'xmlnstbl',
  'protusertbl'
]);

/**
 * Widens bytes to a string one code unit per byte, preserving every byte value exactly.
 *
 * ⚠️ `TextDecoder('latin1')` would NOT do this. Per the Encoding Standard both `latin1` and
 * `iso-8859-1` are labels for **windows-1252**, which remaps 0x80–0x9F to other code points — so
 * decoding that way then reading char codes back would corrupt exactly the bytes a `\'XX` escape is
 * most likely to carry. The scan needs raw byte values; decoding to text happens later, once the
 * declared code page is known.
 * @param {Uint8Array} bytes - The RTF document.
 * @returns {string} One code unit per input byte.
 */
function bytesToScanString(bytes: Uint8Array): string {
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += SCAN_CHUNK) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + SCAN_CHUNK)));
  }
  return parts.join('');
}

/**
 * Builds a decoder for the code page the document declares in `\ansicpg`.
 * @param {string} rtf - The scan string.
 * @returns {TextDecoder} A decoder for the declared code page, or cp1252 when absent or unsupported.
 */
function decoderFor(rtf: string): TextDecoder {
  const declared = /\\ansicpg(\d+)/.exec(rtf)?.[1] ?? '1252';
  const label = CODE_PAGE_LABELS.get(declared) ?? 'windows-1252';
  try {
    return new TextDecoder(label);
  } catch {
    // A runtime without that encoding. cp1252 is always present and is Outlook's own default.
    return new TextDecoder('windows-1252');
  }
}

/**
 * Collects output as a mix of raw bytes and already-decoded text.
 *
 * Both kinds occur and they cannot share one buffer: literal body text and `\'XX` escapes are
 * raw bytes in the document's code page, while `\uN` escapes and `\*\htmltag` markup are already
 * characters. Buffering bytes until a text push forces a flush keeps each run of bytes contiguous,
 * which matters because a multi-byte character in a code page like cp932 is split across two
 * consecutive `\'XX` escapes and only decodes correctly if they are decoded together.
 */
class HtmlAccumulator {
  private readonly parts: string[] = [];
  private bytes: number[] = [];

  /**
   * @param {TextDecoder} decoder - Decoder for the document's declared code page.
   */
  constructor(private readonly decoder: TextDecoder) {}

  /**
   * Appends one raw byte of body text.
   * @param {number} byte - The byte value.
   * @returns {void}
   */
  pushByte(byte: number): void {
    this.bytes.push(byte & 0xff);
  }

  /**
   * Appends already-decoded text, flushing any pending bytes first so ordering is preserved.
   * @param {string} text - The text to append.
   * @returns {void}
   */
  pushText(text: string): void {
    this.flush();
    this.parts.push(text);
  }

  /**
   * Decodes and appends any buffered bytes.
   * @returns {void}
   */
  private flush(): void {
    if (this.bytes.length === 0) return;
    this.parts.push(this.decoder.decode(Uint8Array.from(this.bytes)));
    this.bytes = [];
  }

  /**
   * @returns {string} Everything collected, in order.
   */
  toString(): string {
    this.flush();
    return this.parts.join('');
  }
}

/** Parser state saved and restored across RTF group boundaries. */
interface GroupState {
  htmlrtf: boolean;
  inHtmlTag: boolean;
  unicodeSkip: number;
}

/** Matches a control word with its optional numeric parameter and single delimiting space. */
const CONTROL_WORD = /^\\([a-zA-Z]+)(-?\d+)? ?/;

/**
 * De-encapsulates the HTML body from decompressed RTF.
 *
 * Takes *decompressed* bytes rather than `PidTagRtfCompressed` so the parsing — all of the risk and
 * all of the logic — is testable against synthetic RTF, with no compound-file fixture involved.
 * Decompression is the caller's concern.
 * @param {Uint8Array} rtf - Decompressed RTF bytes.
 * @returns {string | null} The encapsulated HTML, or null when there is none to recover.
 * @example
 * const html = deEncapsulateRtfHtml(decompressMsgRtf(fields.compressedRtf));
 * if (html !== null) render(sanitiseBodyHtml(html, cidMap)); // NEVER render html directly
 */
export function deEncapsulateRtfHtml(rtf: Uint8Array): string | null {
  if (rtf.length === 0 || rtf.length > RTF_MAX_DECOMPRESSED_BYTES) return null;

  try {
    const text = bytesToScanString(rtf);
    // `\fromhtml1` is the marker that this RTF encapsulates HTML at all. Without it the document is
    // genuine RTF — a real body in its own right — and inventing HTML from it would be a fabrication.
    if (!text.includes('\\fromhtml1')) return null;

    const html = parseEncapsulated(text, decoderFor(text));
    return html.trim() === '' ? null : html;
  } catch {
    // Untrusted input; a malformed document is a real outcome, not a defensive afterthought. The
    // adapter's plain-text fallback is what the reader sees.
    return null;
  }
}

/**
 * Unwraps a `PidTagRtfCompressed` stream to plain RTF bytes.
 *
 * Separated from {@link deEncapsulateRtfHtml} so the parser stays a pure function over RTF and can
 * be tested without a compound-file fixture. Failure is reported as null rather than thrown for the
 * same reason as everywhere else here: the bytes are untrusted, and the caller's fallback is the
 * plain-text body.
 * @param {Uint8Array | undefined} compressedRtf - The `PidTagRtfCompressed` property, when present.
 * @returns {Uint8Array | null} Decompressed RTF, or null when absent or unreadable.
 */
export function decompressMsgRtf(compressedRtf: Uint8Array | undefined): Uint8Array | null {
  if (compressedRtf == null || compressedRtf.length === 0) return null;
  try {
    // The library's signature is `number[]` in and `number[]` out, so the typed array is widened
    // going in and narrowed coming back. Converting rather than casting keeps this honest: the
    // arrays really are different types, and a cast would only hide that at the cost of a lie.
    const decompressed = Uint8Array.from(decompressRTF(Array.from(compressedRtf)));
    return decompressed.length === 0 ? null : decompressed;
  } catch {
    return null;
  }
}

/**
 * Convenience over {@link decompressMsgRtf} then {@link deEncapsulateRtfHtml} — the only sequence
 * callers need, expressed once so no caller can perform half of it.
 *
 * ⚠️ The result is untrusted markup. It MUST be sanitised before it reaches the viewer's frame.
 * @param {Uint8Array | undefined} compressedRtf - The `PidTagRtfCompressed` property, when present.
 * @returns {string | null} The encapsulated HTML, or null when there is none to recover.
 */
export function msgRtfToHtml(compressedRtf: Uint8Array | undefined): string | null {
  const rtf = decompressMsgRtf(compressedRtf);
  return rtf === null ? null : deEncapsulateRtfHtml(rtf);
}

/**
 * Walks the RTF, emitting encapsulated HTML and dropping everything that is RTF's own.
 * @param {string} text - The scan string, one code unit per byte.
 * @param {TextDecoder} decoder - Decoder for the declared code page.
 * @returns {string} The recovered HTML.
 */
function parseEncapsulated(text: string, decoder: TextDecoder): string {
  const out = new HtmlAccumulator(decoder);
  const stack: GroupState[] = [];
  let state: GroupState = { htmlrtf: false, inHtmlTag: false, unicodeSkip: 1 };
  // Depth at which an ignorable destination began, or null when not skipping one.
  let skipDepth: number | null = null;
  let i = 0;

  /**
   * Whether body content at the current position belongs in the output.
   * @returns {boolean} True when nothing is suppressing it.
   */
  const emitting = (): boolean => skipDepth === null && (state.inHtmlTag || !state.htmlrtf);

  while (i < text.length) {
    const ch = text[i];

    if (ch === '{') {
      stack.push({ ...state });
      i += 1;
    } else if (ch === '}') {
      const restored = stack.pop();
      if (restored !== undefined) state = restored;
      if (skipDepth !== null && stack.length < skipDepth) skipDepth = null;
      i += 1;
    } else if (ch === '\\') {
      i = handleBackslash(text, i, state, out, emitting, {
        beginSkip: () => {
          skipDepth = stack.length;
        }
      });
    } else if (ch === '\r' || ch === '\n') {
      // Line breaks between control words are RTF's own formatting, never body content.
      i += 1;
    } else {
      if (emitting()) out.pushByte(text.charCodeAt(i));
      i += 1;
    }
  }

  return out.toString();
}

/** Callbacks `handleBackslash` needs to reach back into the walker's state. */
interface BackslashHooks {
  beginSkip: () => void;
}

/**
 * Handles one escape or control word, returning the position to resume scanning from.
 * @param {string} text - The scan string.
 * @param {number} start - Index of the backslash.
 * @param {GroupState} state - Current group state, mutated in place.
 * @param {HtmlAccumulator} out - Output collector.
 * @param {() => boolean} emitting - Whether content is currently being emitted.
 * @param {BackslashHooks} hooks - Walker callbacks.
 * @returns {number} The next index to scan.
 */
function handleBackslash(
  text: string,
  start: number,
  state: GroupState,
  out: HtmlAccumulator,
  emitting: () => boolean,
  hooks: BackslashHooks
): number {
  const word = CONTROL_WORD.exec(text.slice(start));
  if (word !== null) {
    return handleControlWord(text, start, word, state, out, emitting, hooks);
  }

  const symbol = text[start + 1];

  // `\'XX` — one byte of body text in the declared code page.
  if (symbol === "'") {
    const byte = Number.parseInt(text.slice(start + 2, start + 4), 16);
    if (emitting() && !Number.isNaN(byte)) out.pushByte(byte);
    return start + 4;
  }

  // `\*` marks an ignorable destination. `\*\htmltag` carries the HTML and is the one kind that
  // must be READ; every other kind carries RTF's own metadata and is skipped whole.
  if (symbol === '*') {
    const next = start + 2;
    if (!text.startsWith('\\htmltag', next)) hooks.beginSkip();
    return next;
  }

  if (symbol === '\\' || symbol === '{' || symbol === '}') {
    if (emitting()) out.pushText(symbol);
    return start + 2;
  }

  // An escaped line break is a line break in the source, not content.
  if (symbol === '\r' || symbol === '\n') return start + 2;

  return start + 1;
}

/**
 * Applies one control word.
 * @param {string} text - The scan string.
 * @param {number} start - Index of the backslash.
 * @param {RegExpExecArray} word - The matched control word, parameter, and delimiter.
 * @param {GroupState} state - Current group state, mutated in place.
 * @param {HtmlAccumulator} out - Output collector.
 * @param {() => boolean} emitting - Whether content is currently being emitted.
 * @param {BackslashHooks} hooks - Walker callbacks.
 * @returns {number} The next index to scan.
 */
function handleControlWord(
  text: string,
  start: number,
  word: RegExpExecArray,
  state: GroupState,
  out: HtmlAccumulator,
  emitting: () => boolean,
  hooks: BackslashHooks
): number {
  const [matched, name, parameter] = word;
  const next = start + matched.length;

  if (SKIPPED_DESTINATIONS.has(name)) {
    hooks.beginSkip();
    return next;
  }

  switch (name) {
    case 'htmlrtf':
      // `\htmlrtf` opens a run of RTF-only formatting; `\htmlrtf0` closes it.
      state.htmlrtf = parameter !== '0';
      return next;
    case 'htmltag':
      // Everything in this destination is literal HTML, and `\htmlrtf` does not gate it.
      state.inHtmlTag = true;
      state.htmlrtf = false;
      return next;
    case 'uc':
      state.unicodeSkip = Number.parseInt(parameter ?? '1', 10);
      return next;
    case 'u':
      return handleUnicodeEscape(text, next, parameter, state, out, emitting);
    case 'par':
    case 'line':
      if (emitting()) out.pushText('\n');
      return next;
    case 'tab':
      if (emitting()) out.pushText('\t');
      return next;
    default:
      // Every other control word is RTF's own formatting. The HTML already expresses whatever it
      // was describing, so dropping it is the point rather than a gap.
      return next;
  }
}

/**
 * Emits a `\uN` character and steps over the ASCII fallback that trails it.
 *
 * The fallback exists for readers that cannot handle Unicode. Leaving it in would print a literal
 * `?` (or a transliteration) beside the character it was standing in for.
 * @param {string} text - The scan string.
 * @param {number} from - Index just past the control word.
 * @param {string | undefined} parameter - The code point, as RTF's signed 16-bit value.
 * @param {GroupState} state - Current group state.
 * @param {HtmlAccumulator} out - Output collector.
 * @param {() => boolean} emitting - Whether content is currently being emitted.
 * @returns {number} The next index to scan.
 */
function handleUnicodeEscape(
  text: string,
  from: number,
  parameter: string | undefined,
  state: GroupState,
  out: HtmlAccumulator,
  emitting: () => boolean
): number {
  const raw = Number.parseInt(parameter ?? '', 10);
  if (!Number.isNaN(raw) && emitting()) {
    // RTF writes code points above U+7FFF as negative 16-bit values.
    out.pushText(String.fromCodePoint(raw < 0 ? raw + 0x10000 : raw));
  }

  let i = from;
  let skipped = 0;
  while (skipped < state.unicodeSkip && i < text.length) {
    // A fallback may itself be a `\'XX` escape, which is four characters rather than one.
    i += text[i] === '\\' && text[i + 1] === "'" ? 4 : 1;
    skipped += 1;
  }
  return i;
}
